import { Controller } from "@hotwired/stimulus";

// Development-only helper for filling blank analyzer review fields.
export default class extends Controller {
  static values = {
    dummyData: Object,
  };

  // Fills blank visible form controls with development dummy values.
  // Called by the "Fill blank fields" button through data-action.
  fillBlankFields() {
    this.blankEnabledSelects().forEach((select) => {
      const option = this.randomSelectableOption(select);
      if (!option) return;

      select.value = option.value;
      this.dispatchFieldEvents(select);
    });

    this.blankEnabledTextFields().forEach((field) => {
      field.value = this.dummyValueFor(field);
      this.dispatchFieldEvents(field);
    });
  }

  // Finds select controls that can safely receive a random option.
  // Called by fillBlankFields().
  // Returns an array, for example [HTMLSelectElement, HTMLSelectElement].
  blankEnabledSelects() {
    return Array.from(this.element.querySelectorAll("select")).filter((select) => (
      !this.fieldIsUnavailable(select) && this.fieldIsBlank(select)
    ));
  }

  // Finds text-like controls that can safely receive dummy text or numbers.
  // Called by fillBlankFields().
  // Returns an array, for example [HTMLInputElement, HTMLTextAreaElement].
  blankEnabledTextFields() {
    return Array.from(this.element.querySelectorAll("input, textarea")).filter((field) => (
      !this.fieldIsUnavailable(field) && this.fieldIsBlank(field) && this.fieldAcceptsText(field)
    ));
  }

  // Checks whether a control should be skipped because it is hidden or disabled.
  // Called by blankEnabledSelects() and blankEnabledTextFields().
  // Returns a boolean, for example true when the field is inside a hidden layout branch.
  fieldIsUnavailable(field) {
    return field.disabled || field.hidden || field.closest("[hidden]");
  }

  // Checks whether a control has no user or analyzer value yet.
  // Called by blankEnabledSelects() and blankEnabledTextFields().
  // Returns a boolean, for example true when field.value is "".
  fieldIsBlank(field) {
    return field.value.trim() === "";
  }

  // Checks whether an input or textarea accepts ordinary string dummy data.
  // Called by blankEnabledTextFields().
  // Returns a boolean, for example false for hidden, file, submit, or radio inputs.
  fieldAcceptsText(field) {
    if (field.tagName.toLowerCase() === "textarea") return true;

    return !["button", "checkbox", "file", "hidden", "image", "radio", "reset", "submit"].includes(field.type);
  }

  // Picks one non-blank option from a select control.
  // Called by fillBlankFields().
  // Returns an HTMLOptionElement or null, for example the "PASS" option.
  randomSelectableOption(select) {
    const options = Array.from(select.options).filter((option) => (
      !option.disabled && option.value.trim() !== ""
    ));

    if (options.length === 0) return null;

    return options[Math.floor(Math.random() * options.length)];
  }

  // Chooses the correct dummy value for a specific form control.
  // Called by fillBlankFields().
  // Returns a string, for example "Lift 3", "94.25", or "BAC".
  dummyValueFor(field) {
    const readingKey = this.readingKeyFor(field);
    if (readingKey) return this.dummyDataValue.readings?.[readingKey] || this.fallbackValueFor(field);

    return this.dummyDataValue.fields?.[this.attributeNameFor(field)] || this.fallbackValueFor(field);
  }

  // Extracts a readings JSON key from a nested readings input name.
  // Called by dummyValueFor().
  // Returns a string or undefined, for example "percent_m".
  readingKeyFor(field) {
    return field.name.match(/^gauge_reading\[readings\]\[([^\]]+)\]$/)?.[1];
  }

  // Extracts a GaugeReading attribute name from a standard input name.
  // Called by dummyValueFor().
  // Returns a string or undefined, for example "surface_type_other".
  attributeNameFor(field) {
    return field.name.match(/^gauge_reading\[([^\]]+)\]$/)?.[1];
  }

  // Provides a generic dummy value when no field-specific value exists.
  // Called by dummyValueFor().
  // Returns a string, for example "112.40" for decimal fields or "Field Check" for text fields.
  fallbackValueFor(field) {
    if (field.inputMode === "decimal" || field.pattern) return this.dummyDataValue.fallbackDecimal;

    return this.dummyDataValue.fallbackText;
  }

  // Notifies Stimulus, browser validation, and any listeners that a field changed.
  // Called by fillBlankFields() after assigning a select or text field value.
  dispatchFieldEvents(field) {
    field.dispatchEvent(new Event("input", { bubbles: true }));
    field.dispatchEvent(new Event("change", { bubbles: true }));
  }
}
